import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { Failed, PendingButton, SkeletonRows } from '../components/pending'
import { useLayer } from '../components/layers'
import o from '../components/Overlay.module.css'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import styles from './IntegrationsPage.module.css'
import own from './ApiKeysPage.module.css'

// The Dataphyll API card's "View API keys and docs" (§7d h 1321-1330; §9.5
// h 2917: "API keys: name/purpose, scopes, issuer. Secrets shown once.").
//
// A key is issued with a name that says what it is for and at least one scope
// (D4: stock:read, reports:read), and the whole key is shown once, in the
// answer to that press: the server keeps only a hash of it, so nothing, this
// page included, can show it again. A key is revoked, never edited or deleted,
// and its scopes cannot be changed: to change access, revoke it and issue
// another. The scopes offered come from the server (scopes_available), so this
// page cannot offer one the API would refuse.

interface ScopeInfo {
  key: string
  label: string
  description: string
}

interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string[]
  issued: { by: string; at: string }
  revoked: { by: string; at: string } | null
}

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })

const errText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.')

export function ApiKeysPage() {
  const { user } = useAuth()
  const [keys, setKeys] = useState<ApiKey[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [available, setAvailable] = useState<ScopeInfo[]>([])
  const [chosen, setChosen] = useState<string[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<{ at: string; text: string } | null>(null)
  // The key just issued. It lives only here, and only until Done.
  const [issued, setIssued] = useState<{ key: ApiKey; secret: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const r = await apiJson<{ keys: ApiKey[]; scopes_available?: ScopeInfo[] }>(user, '/api/api-keys')
      setKeys(r.keys)
      setAvailable(r.scopes_available || [])
    } catch (err) {
      setLoadError(errText(err))
    }
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  const issue = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || chosen.length === 0 || busy) return
    setBusy('issue')
    setFailed(null)
    try {
      const r = await apiJson<{ key: ApiKey; secret: string }>(user, '/api/api-keys', { method: 'POST', body: { name: name.trim(), scopes: chosen } })
      setIssued(r)
      setCopied(false)
      setName('')
      setChosen([])
      setKeys((list) => [r.key, ...(list || [])])
    } catch (err) {
      setFailed({ at: 'issue', text: `${errText(err)}` })
    } finally {
      setBusy(null)
    }
  }

  const revoke = async (k: ApiKey) => {
    setBusy(k.id)
    setFailed(null)
    try {
      const r = await apiJson<{ key: ApiKey }>(user, `/api/api-keys/${k.id}/revoke`, { method: 'POST', body: {} })
      setKeys((list) => (list || []).map((x) => (x.id === k.id ? r.key : x)))
      setConfirming(null)
    } catch (err) {
      setFailed({ at: k.id, text: errText(err) })
      load()
    } finally {
      setBusy(null)
    }
  }

  const copy = async () => {
    if (!issued) return
    try {
      await navigator.clipboard?.writeText(issued.secret)
      setCopied(true)
    } catch {
      /* the clipboard can be refused; the key is still selectable */
    }
  }

  return (
    <>
      <PageHeader
        title="Dataphyll API"
        actions={
          <Link to="/integrations" className={headerStyles.btn}>
            ← Integrations
          </Link>
        }
      />
      <PageBody>
        <div className={own.notice} data-testid="api-notice">
        <p>
          <strong>A key reads your workspace’s data through the external API, and only what its scopes allow.</strong> It cannot sign in to
          CONTROL, change anything, or issue other keys. Send it as <code>Authorization: Bearer &lt;key&gt;</code>.
        </p>
        <p>
          <code>GET /api/external/v1/stock</code> needs Read stock. <code>GET /api/external/v1/reports/daily-harvest?from=YYYY-MM-DD&amp;to=YYYY-MM-DD</code>{' '}
          needs Read reports.
        </p>
        <p>A key’s access is fixed when it is issued. To change access, revoke this key and issue a new one.</p>
      </div>

      {loadError ? (
        <div className={styles.error} role="alert">
          <p>{loadError}</p>
          {!/permission set/.test(loadError) && <button onClick={load}>Retry</button>}
        </div>
      ) : (
        <>
          <form className={`${styles.card} ${own.issue}`} onSubmit={issue} aria-label="Issue a key">
            <label className={styles.sectionLabel} htmlFor="api-key-name">
              What is this key for?
            </label>
            <div className={styles.keyRow}>
              <input
                id="api-key-name"
                className={styles.input}
                value={name}
                maxLength={120}
                placeholder="e.g. Packhouse BI export"
                onChange={(e) => setName(e.target.value)}
              />
              <PendingButton type="submit" className={styles.primary} busy={busy === 'issue'} pending="Issuing…" disabled={!name.trim() || chosen.length === 0 || !!busy}>
                Issue key
              </PendingButton>
            </div>
            <p className={styles.hint}>The name is how you will recognise the key later, and who issued it is recorded with it.</p>
            <fieldset className={own.scopes} data-testid="api-key-scopes">
              <legend className={styles.sectionLabel}>What may this key do?</legend>
              {available.map((sc) => (
                <label key={sc.key} className={own.scope} htmlFor={`api-scope-${sc.key}`}>
                  <input
                    id={`api-scope-${sc.key}`}
                    type="checkbox"
                    checked={chosen.includes(sc.key)}
                    onChange={(e) => setChosen((c) => (e.target.checked ? [...c, sc.key] : c.filter((x) => x !== sc.key)))}
                  />
                  <span>
                    <strong>{sc.label}</strong> <code>{sc.key}</code>
                    <span className={own.sub}>{sc.description}</span>
                  </span>
                </label>
              ))}
              <p className={styles.hint}>Choose at least one. It cannot be changed afterwards.</p>
            </fieldset>
            {failed?.at === 'issue' && <Failed text={`${failed.text}`} />}
          </form>

          {issued && (
            <section className={own.shown} role="status" aria-label="The key just issued" data-testid="api-key-shown">
              <h2>Copy this key now: {issued.key.name}</h2>
              <p>
                It is shown once. CONTROL keeps only a fingerprint of it, so it cannot be shown again, by this page or by Dataphyll. If it is
                lost, revoke it and issue another.
              </p>
              <div className={styles.keyRow}>
                <input className={`${styles.input} ${own.secret}`} readOnly value={issued.secret} aria-label="API key" onFocus={(e) => e.target.select()} />
                <button type="button" className={styles.button} onClick={copy}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button type="button" className={styles.primary} onClick={() => setIssued(null)}>
                  Done
                </button>
              </div>
            </section>
          )}

          <section className={own.list} aria-label="Keys issued">
            <h2 className={own.listHead}>Keys issued</h2>
            {keys === null ? (
              <SkeletonRows rowClass={own.row} cols={5} rows={3} label="Loading keys" />
            ) : keys.length === 0 ? (
              <div className={styles.empty}>
                <p>No keys have been issued.</p>
              </div>
            ) : (
              <div className={own.tableCard}>
              <table className={own.table} data-testid="api-keys">
                <thead>
                  <tr>
                    <th>For</th>
                    <th>Key</th>
                    <th>Scopes</th>
                    <th>Issued</th>
                    <th>State</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} data-testid="api-key-row" className={k.revoked ? own.revokedRow : undefined}>
                      <td>{k.name}</td>
                      <td>
                        <code>{k.prefix}…</code>
                      </td>
                      <td>{k.scopes.length ? k.scopes.join(', ') : 'None (opens nothing)'}</td>
                      <td>
                        {k.issued.by}
                        <div className={own.sub}>{when(k.issued.at)}</div>
                      </td>
                      <td>
                        {k.revoked ? (
                          <>
                            <span className={`${styles.status} ${styles.pillMuted}`}>Revoked</span>
                            <div className={own.sub}>
                              by {k.revoked.by}, {when(k.revoked.at)}
                            </div>
                          </>
                        ) : (
                          <span className={`${styles.status} ${styles.pillGood}`}>Live</span>
                        )}
                      </td>
                      <td className={own.actions}>
                        {!k.revoked &&
                          (confirming === k.id ? (
                            <RevokeDialog
                              k={k}
                              busy={busy === k.id}
                              error={failed?.at === k.id ? failed.text : null}
                              onRevoke={() => revoke(k)}
                              onKeep={() => {
                                setConfirming(null)
                                setFailed(null)
                              }}
                            />
                          ) : (
                            <button type="button" className={own.revokeBtn} disabled={!!busy} onClick={() => setConfirming(k.id)}>
                              Revoke…
                            </button>
                          ))}
                        {failed?.at === k.id && confirming !== k.id && <Failed text={failed.text} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </section>
        </>
      )}
      </PageBody>
    </>
  )
}

// The revoke confirm (V4 apiRevoke): a narrow modal, rendered inside the row so
// it stays with the key it is about. Keep it closes it and sends nothing.
function RevokeDialog({ k, busy, error, onRevoke, onKeep }: { k: ApiKey; busy: boolean; error: string | null; onRevoke: () => void; onKeep: () => void }) {
  const panelEl = useRef<HTMLDivElement>(null)
  const layer = useLayer(true, () => !busy && onKeep(), { el: panelEl, modal: true, busy })
  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && layer.requestClose()}>
      <div ref={panelEl} tabIndex={-1} role="dialog" aria-label={`Revoke ${k.name}?`} className={`${o.modal} ${own.revokeModal}`}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Revoke {k.name}?</h2>
            <p className={o.sub}>
              Any app calling with this key stops working immediately, and it cannot be undone: a replacement is a new key. Data already read
              stays as it is. Issued {when(k.issued.at)} by {k.issued.by}.
            </p>
          </div>
        </div>
        {error && (
          <div className={o.body}>
            <Failed text={error} />
          </div>
        )}
        <div className={`${o.foot} ${own.revokeFoot}`}>
          <button type="button" className={o.ghost} disabled={busy} onClick={layer.requestClose}>
            Keep it
          </button>
          <PendingButton className={o.dangerBtn} busy={busy} pending="Revoking…" disabled={busy} onClick={onRevoke}>
            Revoke key
          </PendingButton>
        </div>
      </div>
    </div>
  )
}
